import { useGuestSiteTelemetry } from '@apps/guest/GuestSite.telemetry';
import { EventPageFragment, EventPageType } from '@graphql/generated';
import { useCallback, useEffect, useState } from 'react';
import { useHistory } from '@shared/core';
import { useEventUserRole } from '@shared/components/AuthProvider';
import { GuestSiteQueryParams, useGuestSiteQueryParams } from './useGuestSiteQueryParams';
import { CUSTOM_PAGE_PATH } from '@apps/guest/routes/GuestSite/GuestSite.routes';
import { useGuestSitePageViewedDuration } from './useGuestSitePageViewedDuration';

export const getSlugFromPathname = (pathname: string) => {
  const isCustomPagePath = pathname.split('/')[2] === CUSTOM_PAGE_PATH;
  const pagePath = isCustomPagePath ? pathname.split('/')[3] : pathname.split('/')[2];
  return { pagePath, isCustomPagePath };
};

export const getPageInfo = (pages: Array<EventPageFragment>, pathname: string) => {
  const { pagePath, isCustomPagePath } = getSlugFromPathname(pathname);
  const pageFound = pages.find(p => p.pageSlug === pagePath);
  return { pageSlug: isCustomPagePath ? EventPageType.custom : pageFound?.pageSlug, pageFound };
};

export const useGuestSitePageChanged = (pages: Array<EventPageFragment>) => {
  const telemetry = useGuestSiteTelemetry() as unknown as Record<string, { enter: (role: string, args: GuestSiteQueryParams) => void }>;
  const [currentPageSlug, setCurrentPageSlug] = useState<string>();
  const { isAdmin } = useEventUserRole();
  const history = useHistory();
  const queryParams = useGuestSiteQueryParams();
  const { trackPageVisitDuration } = useGuestSitePageViewedDuration(pages);

  const trackPage = useCallback(
    locationPathName => {
      const { pageSlug, pageFound } = getPageInfo(pages, locationPathName);
      if (pageFound && pageSlug && telemetry[pageSlug] && currentPageSlug !== pageFound.pageSlug) {
        telemetry[pageSlug].enter(isAdmin ? 'admin' : 'guest', { ...queryParams, slug: pageFound.pageSlug });
        setCurrentPageSlug(pageFound.pageSlug);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pages, history, currentPageSlug],
  );

  useEffect(() => {
    if (history.location.pathname) {
      trackPage(history.location.pathname);
      trackPageVisitDuration(history.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history.location.pathname]);
};
